





import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Camera, Loader2, Trash2 } from "lucide-react";

import {
  getGroupMembersApi,
  addGroupMemberApi,
  removeGroupMemberApi,
  updateChatGroupApi,
} from "../../app/api";
import { formatChatDateTime, getGroupImageUrl, getStoredUser } from "./chatUtils";

export default function GroupMembersModal({
  group,
  onClose,
  employees,
  isAdmin,
  onGroupUpdated,
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState("");
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [groupName, setGroupName] = useState(group.name || "");
  const [groupImage, setGroupImage] = useState(null);
  const [groupImagePreview, setGroupImagePreview] = useState(getGroupImageUrl(group));
  const [groupError, setGroupError] = useState("");

  const currentUser = getStoredUser();
  const canManage = isAdmin || group.created_by === currentUser.id;

  // ✅ FETCH MEMBERS (UPDATED)
  const fetchMembers = async () => {
    try {
      const res = await getGroupMembersApi(group.id);

      if (res?.success) {
        setMembers(res.members || []);
      }
    } catch (err) {
      console.error("Fetch members error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [group.id]);

  useEffect(() => {
    setGroupName(group.name || "");
    setGroupImage(null);
    setGroupImagePreview(getGroupImageUrl(group));
    setGroupError("");
  }, [group]);

  // ✅ REMOVE MEMBER (UPDATED)
  const handleRemove = async (userId) => {
    try {
      const res = await removeGroupMemberApi(group.id, userId);

      if (res?.success) {
        setMembers((prev) => prev.filter((m) => m.id !== userId));
      } else {
        alert(res?.message || "Failed to remove member");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to remove member.");
    } finally {
      setMemberToRemove(null);
    }
  };

  // ✅ ADD MEMBER (UPDATED)
  const handleAddMember = async () => {
    if (!selectedUserToAdd) return;

    try {
      const res = await addGroupMemberApi(group.id, selectedUserToAdd);

      if (res?.success) {
        setShowAdd(false);
        setSelectedUserToAdd("");
        fetchMembers();
      } else {
        alert(res?.message || "Failed to add member");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to add member.");
    }
  };

  const handleGroupImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");

    if (!isImage) {
      setGroupImage(null);
      setGroupError("Please select an image file.");
      event.target.value = "";
      return;
    }

    setGroupError("");
    setGroupImage(file);
    setGroupImagePreview(URL.createObjectURL(file));
  };

  const handleSaveGroup = async () => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      setGroupError("Group name is required.");
      return;
    }

    setSavingGroup(true);
    setGroupError("");

    try {
      const payload = new FormData();
      payload.append("name", trimmedName);
      if (groupImage) payload.append("image", groupImage);

      const res = await updateChatGroupApi(group.id, payload);

      if (res?.success) {
        const updatedGroup = res.group || res.data || {
          ...group,
          name: trimmedName,
          image: groupImagePreview,
        };

        onGroupUpdated?.({ ...group, ...updatedGroup, name: updatedGroup.name || trimmedName });
        setGroupImage(null);
      } else {
        setGroupError(res?.message || "Failed to update group.");
      }
    } catch (err) {
      console.error("Update group error:", err);
      setGroupError("Failed to update group.");
    } finally {
      setSavingGroup(false);
    }
  };

  const availableEmployees = employees.filter(
    (emp) => !members.some((m) => m.id === emp.id)
  );

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-white/20 p-6 rounded-2xl w-[440px] max-w-[calc(100vw-24px)] text-white">
        
        {/* HEADER */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Group Details</h3>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white transition"
          >
            ✕
          </button>
        </div>

        <div className="mb-5 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
          <div className="flex items-center gap-4">
            <label className="group relative flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-violet-300/25 bg-violet-600 text-white">
              {groupImagePreview ? (
                <img
                  src={groupImagePreview}
                  alt={groupName || "Group"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-xl font-bold">
                  {groupName?.charAt(0)?.toUpperCase() || "G"}
                </span>
              )}
              <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition group-hover:opacity-100">
                <Camera size={18} />
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleGroupImageChange}
                className="hidden"
              />
            </label>

            <div className="min-w-0 flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-white/45">
                Group Name
              </label>
              <input
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-violet-300/60 focus:ring-4 focus:ring-violet-500/10"
                placeholder="Group name"
              />
              <p className="mt-1 text-[11px] text-white/40">
                Upload any image. It will be converted to WebP automatically.
              </p>
              <div className="mt-3 rounded-lg border border-white/10 bg-black/15 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-white/35">
                  Created
                </p>
                <p className="mt-0.5 text-xs font-medium text-white/70">
                  {formatChatDateTime(
                    group?.created_at || group?.createdAt || group?.created_on || group?.createdOn
                  )}
                </p>
              </div>
            </div>
          </div>

          {groupError && (
            <p className="mt-3 rounded-lg border border-rose-300/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
              {groupError}
            </p>
          )}

          <button
            onClick={handleSaveGroup}
            disabled={savingGroup}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {savingGroup && <Loader2 size={15} className="animate-spin" />}
            Save Group Details
          </button>
        </div>

        {/* MEMBERS LIST */}
        {loading ? (
          <p className="text-center text-sm opacity-50 my-10">Loading...</p>
        ) : (
          <div className="max-h-[300px] overflow-y-auto mb-4 border border-white/10 rounded-lg p-2 custom-scrollbar">
            {members.length === 0 ? (
              <p className="text-center text-sm opacity-50 p-2">
                No members found
              </p>
            ) : (
              members.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between p-2 hover:bg-white/10 rounded-lg transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center font-bold text-sm">
                      {m.first_name?.charAt(0)}
                    </div>
                    <span className="text-sm font-medium">
                      {m.first_name} {m.last_name}
                    </span>
                  </div>

                  {canManage && m.id !== currentUser.id && (
                    <button
                      onClick={() => setMemberToRemove(m.id)}
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-md transition"
                      title="Remove Member"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* ADD MEMBER */}
        {canManage && (
          <div className="border-t border-white/10 pt-4 mt-2">
            {!showAdd ? (
              <button
                onClick={() => setShowAdd(true)}
                className="w-full py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition"
              >
                + Add Member
              </button>
            ) : (
              <div className="flex gap-2">
                <select
                  className="flex-1 bg-white/10 border border-white/20 outline-none rounded-lg px-3 py-2 text-sm text-white"
                  value={selectedUserToAdd}
                  onChange={(e) => setSelectedUserToAdd(e.target.value)}
                >
                  <option value="" className="text-black">
                    Select an employee...
                  </option>
                  {availableEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id} className="text-black">
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>

                <button
                  onClick={handleAddMember}
                  className="px-4 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm transition"
                >
                  Add
                </button>

                <button
                  onClick={() => setShowAdd(false)}
                  className="px-3 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}

        {/* REMOVE CONFIRM MODAL */}
        {memberToRemove && (
          <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-gray-800 border border-white/10 p-6 rounded-2xl w-[320px] text-center shadow-xl">
              <h4 className="text-lg font-bold text-white mb-2">
                Remove Member
              </h4>
              <p className="text-sm text-white/70 mb-6">
                Are you sure you want to remove this member from the group?
              </p>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setMemberToRemove(null)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition"
                >
                  Cancel
                </button>

                <button
                  onClick={() => handleRemove(memberToRemove)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}

// import { useState, useEffect } from "react";
// import { createPortal } from "react-dom";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { Trash2 } from "lucide-react";

// export default function GroupMembersModal({ group, onClose, employees, isAdmin }) {
//   const [members, setMembers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showAdd, setShowAdd] = useState(false);
//   const [selectedUserToAdd, setSelectedUserToAdd] = useState("");
//   const [memberToRemove, setMemberToRemove] = useState(null);

//   const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
//   const canManage = isAdmin || group.created_by === currentUser.id;

//   const fetchMembers = async () => {
//     try {
//       const token = Cookies.get("token") || localStorage.getItem("token");
//       const res = await axios.get(`${API_BASE_URL}/chat/groups/${group.id}/members`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       if (res.data.success) {
//         setMembers(res.data.members || []);
//       }
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchMembers();
//   }, [group.id]);

//   const handleRemove = async (userId) => {
//     try {
//       const token = Cookies.get("token") || localStorage.getItem("token");
//       await axios.delete(`${API_BASE_URL}/chat/groups/${group.id}/members/${userId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setMembers((prev) => prev.filter((m) => m.id !== userId));
//       setMemberToRemove(null);
//     } catch (err) {
//       alert("Failed to remove member.");
//       setMemberToRemove(null);
//     }
//   };

//   const handleAddMember = async () => {
//     if (!selectedUserToAdd) return;
//     try {
//       const token = Cookies.get("token") || localStorage.getItem("token");
//       await axios.post(
//         `${API_BASE_URL}/chat/groups/${group.id}/members`,
//         { userId: selectedUserToAdd },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       setShowAdd(false);
//       setSelectedUserToAdd("");
//       fetchMembers();
//     } catch (err) {
//       alert("Failed to add member.");
//     }
//   };

//   const availableEmployees = employees.filter(
//     (emp) => !members.some((m) => m.id === emp.id)
//   );

//   return createPortal(
//     <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
//       <div className="bg-gray-900 border border-white/20 p-6 rounded-2xl w-[400px] text-white">
//         <div className="flex justify-between items-center mb-4">
//           <h3 className="text-xl font-bold">{group.name} Members</h3>
//           <button onClick={onClose} className="text-white/50 hover:text-white transition">✕</button>
//         </div>

//         {loading ? (
//           <p className="text-center text-sm opacity-50 my-10">Loading...</p>
//         ) : (
//           <div className="max-h-[300px] overflow-y-auto mb-4 border border-white/10 rounded-lg p-2 custom-scrollbar">
//             {members.length === 0 ? (
//               <p className="text-center text-sm opacity-50 p-2">No members found</p>
//             ) : (
//               members.map((m) => (
//                 <div key={m.id} className="flex items-center justify-between p-2 hover:bg-white/10 rounded-lg transition">
//                   <div className="flex items-center gap-3">
//                     <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center font-bold text-sm">
//                       {m.first_name?.charAt(0)}
//                     </div>
//                     <span className="text-sm font-medium">{m.first_name} {m.last_name}</span>
//                   </div>
//                   {canManage && m.id !== currentUser.id && (
//                     <button
//                       onClick={() => setMemberToRemove(m.id)}
//                       className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-md transition"
//                       title="Remove Member"
//                     >
//                       <Trash2 size={14} />
//                     </button>
//                   )}
//                 </div>
//               ))
//             )}
//           </div>
//         )}

//         {canManage && (
//           <div className="border-t border-white/10 pt-4 mt-2">
//             {!showAdd ? (
//               <button
//                 onClick={() => setShowAdd(true)}
//                 className="w-full py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition"
//               >
//                 + Add Member
//               </button>
//             ) : (
//               <div className="flex gap-2">
//                 <select
//                   className="flex-1 bg-white/10 border border-white/20 outline-none rounded-lg px-3 py-2 text-sm text-white"
//                   value={selectedUserToAdd}
//                   onChange={(e) => setSelectedUserToAdd(e.target.value)}
//                 >
//                   <option value="" className="text-black">Select an employee...</option>
//                   {availableEmployees.map((emp) => (
//                     <option key={emp.id} value={emp.id} className="text-black">
//                       {emp.first_name} {emp.last_name}
//                     </option>
//                   ))}
//                 </select>
//                 <button
//                   onClick={handleAddMember}
//                   className="px-4 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm transition"
//                 >
//                   Add
//                 </button>
//                 <button
//                   onClick={() => setShowAdd(false)}
//                   className="px-3 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition"
//                 >
//                   ✕
//                 </button>
//               </div>
//             )}
//           </div>
//         )}

//         {memberToRemove && (
//           <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
//             <div className="bg-gray-800 border border-white/10 p-6 rounded-2xl w-[320px] text-center shadow-xl">
//               <h4 className="text-lg font-bold text-white mb-2">Remove Member</h4>
//               <p className="text-sm text-white/70 mb-6">Are you sure you want to remove this member from the group?</p>
//               <div className="flex gap-3 justify-center">
//                 <button
//                   onClick={() => setMemberToRemove(null)}
//                   className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={() => handleRemove(memberToRemove)}
//                   className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition"
//                 >
//                   Confirm
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}

//       </div>
//     </div>,
//     document.body
//   );
// }
